import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, Clock, Tag, ArrowLeft, Share2, ChevronRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { useDynamicSEO } from '../hooks/useSEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_INFO = {
  'immobilienmarkt': { name: 'Immobilienmarkt', color: 'bg-blue-500', icon: '📊' },
  'tips-tricks': { name: 'Tipps und Tricks', color: 'bg-green-500', icon: '💡' },
  'bauprojekte': { name: 'Bauprojekte', color: 'bg-orange-500', icon: '🏗️' },
  'news-trends': { name: 'News & Trends', color: 'bg-purple-500', icon: '📰' }
};

export const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dynamic SEO for individual blog posts
  useDynamicSEO({
    title: post ? `${post.title} | LIVAG AG Blog` : 'Blog | LIVAG AG',
    description: post?.meta_description || post?.excerpt || '',
    canonical: post ? `https://www.livag.ch/blog/${post.slug}` : 'https://www.livag.ch/blog',
    ogImage: null
  });

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/blog/posts/public/${slug}`);
      if (!response.ok) {
        if (response.status === 404) {
          setError('Dieser Blogbeitrag wurde nicht gefunden.');
        } else {
          throw new Error('Failed to fetch post');
        }
        return;
      }
      const data = await response.json();
      setPost(data);
      
      // Fetch related posts from same category
      const relatedResponse = await fetch(`${API_URL}/api/blog/posts/category/${data.category}`);
      if (relatedResponse.ok) {
        const relatedData = await relatedResponse.json();
        setRelatedPosts(relatedData.filter(p => p.id !== data.id).slice(0, 3));
      }
    } catch (error) {
      console.error('Error fetching post:', error);
      setError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-CH', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const estimateReadTime = (content) => {
    const words = content?.split(/\s+/).length || 0;
    return Math.max(1, Math.ceil(words / 200));
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: window.location.href,
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link in Zwischenablage kopiert!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-20">
        <div className="max-w-4xl mx-auto px-4">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-3/4 mb-4" />
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-8" />
            <div className="h-96 bg-gray-200 rounded mb-8" />
            <div className="space-y-4">
              <div className="h-4 bg-gray-200 rounded" />
              <div className="h-4 bg-gray-200 rounded" />
              <div className="h-4 bg-gray-200 rounded w-5/6" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="text-6xl mb-4">😕</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{error}</h1>
          <button
            onClick={() => navigate('/blog')}
            className="inline-flex items-center gap-2 text-[#264F77] font-semibold hover:underline"
          >
            <ArrowLeft size={20} />
            Zurück zum Blog
          </button>
        </div>
      </div>
    );
  }

  if (!post) return null;

  const categoryInfo = CATEGORY_INFO[post.category] || { name: post.category, color: 'bg-gray-500', icon: '📄' };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* JSON-LD BlogPosting Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": post.title,
        "description": post.meta_description || post.excerpt,
        "datePublished": post.published_at,
        "dateModified": post.updated_at || post.published_at,
        "author": {
          "@type": "Organization",
          "name": "LIVAG AG",
          "url": "https://www.livag.ch"
        },
        "publisher": {
          "@type": "Organization",
          "name": "LIVAG AG",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.livag.ch/og-image.jpg"
          }
        },
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://www.livag.ch/blog/${post.slug}`
        },
        "articleSection": categoryInfo.name,
        "inLanguage": "de-CH",
        "wordCount": post.content?.split(/\s+/).length || 0
      }) }} />

      {/* JSON-LD Breadcrumb for this post */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Startseite", "item": "https://www.livag.ch/" },
          { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://www.livag.ch/blog" },
          { "@type": "ListItem", "position": 3, "name": categoryInfo.name, "item": `https://www.livag.ch/blog?kategorie=${post.category}` },
          { "@type": "ListItem", "position": 4, "name": post.title, "item": `https://www.livag.ch/blog/${post.slug}` }
        ]
      }) }} />
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <nav className="flex items-center gap-2 text-sm text-gray-500">
            <Link to="/" className="hover:text-[#264F77]">Home</Link>
            <ChevronRight size={14} />
            <Link to="/blog" className="hover:text-[#264F77]">Blog</Link>
            <ChevronRight size={14} />
            <Link to={`/blog?kategorie=${post.category}`} className="hover:text-[#264F77]">
              {categoryInfo.name}
            </Link>
            <ChevronRight size={14} />
            <span className="text-gray-900 truncate max-w-[200px]">{post.title}</span>
          </nav>
        </div>
      </div>

      {/* Article Header */}
      <article className="max-w-4xl mx-auto px-4 py-12">
        {/* Category Badge */}
        <div className="mb-4">
          <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-white text-sm font-medium ${categoryInfo.color}`}>
            <span>{categoryInfo.icon}</span>
            {categoryInfo.name}
          </span>
        </div>

        {/* Title */}
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
          {post.title}
        </h1>

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-4 text-gray-500 mb-8 pb-8 border-b">
          <span className="flex items-center gap-2">
            <Calendar size={18} />
            {formatDate(post.published_at)}
          </span>
          <span className="flex items-center gap-2">
            <Clock size={18} />
            {estimateReadTime(post.content)} Min. Lesezeit
          </span>
          <button 
            onClick={handleShare}
            className="flex items-center gap-2 text-[#264F77] hover:underline ml-auto"
          >
            <Share2 size={18} />
            Teilen
          </button>
        </div>

        {/* Featured Image */}
        {(post.image_base64 || post.image_url) && (
          <div className="mb-10 rounded-xl overflow-hidden shadow-lg">
            <img 
              src={post.image_url || `data:image/png;base64,${post.image_base64}`}
              alt={`${post.title} – ${categoryInfo.name} | LIVAG AG Blog`}
              width="800"
              height="450"
              loading="lazy"
              className="w-full h-auto object-cover"
            />
          </div>
        )}

        {/* Excerpt */}
        {post.excerpt && (
          <p className="text-xl text-gray-600 mb-8 leading-relaxed font-medium">
            {post.excerpt}
          </p>
        )}

        {/* Content */}
        <div className="prose prose-lg max-w-none prose-headings:text-[#264F77] prose-a:text-[#264F77] prose-strong:text-gray-900 prose-p:text-justify prose-li:text-justify">
          <ReactMarkdown>{post.content}</ReactMarkdown>
        </div>

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="mt-10 pt-8 border-t">
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Tags</h3>
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag, i) => (
                <span 
                  key={i} 
                  className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-gray-200 transition-colors"
                >
                  <Tag size={14} />
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Author/CTA Section */}
        <div className="mt-10 p-8 bg-gradient-to-br from-[#264F77] to-[#1e3d5c] rounded-xl text-white">
          <h3 className="text-2xl font-bold mb-3">Haben Sie Fragen?</h3>
          <p className="text-gray-300 mb-6">
            Unsere Experten bei LIVAG stehen Ihnen gerne für eine persönliche Beratung zur Verfügung.
          </p>
          <Link 
            to="/kontakt"
            className="inline-flex items-center gap-2 bg-white text-[#264F77] px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
          >
            Jetzt Immobilie bewerten lassen
          </Link>
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="bg-white py-16 border-t">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">Weitere Artikel</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.map((relPost) => (
                <Link 
                  key={relPost.id}
                  to={`/blog/${relPost.slug}`}
                  className="group bg-gray-50 rounded-lg overflow-hidden hover:shadow-lg transition-all"
                >
                  <div className="h-40 bg-gray-200 overflow-hidden">
                    {relPost.has_image || relPost.image_url ? (
                      <img 
                        src={relPost.image_url || `data:image/png;base64,${relPost.image_base64}`}
                        alt={relPost.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className={`w-full h-full ${categoryInfo.color} flex items-center justify-center`}>
                        <span className="text-4xl opacity-50">{categoryInfo.icon}</span>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 group-hover:text-[#264F77] line-clamp-2 transition-colors">
                      {relPost.title}
                    </h3>
                    <p className="text-sm text-gray-500 mt-2">
                      {formatDate(relPost.published_at)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Back to Blog */}
      <div className="bg-gray-50 py-8 border-t">
        <div className="max-w-4xl mx-auto px-4">
          <Link 
            to="/blog"
            className="inline-flex items-center gap-2 text-[#264F77] font-semibold hover:gap-3 transition-all"
          >
            <ArrowLeft size={20} />
            Zurück zum Blog
          </Link>
        </div>
      </div>
    </div>
  );
};
